import React, { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewAPI, createQueryKey } from '../../services/api';
// import Stars from './Stars';
import RatingHistogram from './RatingHistogram';
import ReviewsToolbar from './ReviewsToolbar';
import ReviewCard from './ReviewCard';
import { useAuth } from '../../contexts/AuthContext';

export default function ReviewsSection({ projectId, onOpenReview, isTemplate = false }) {
  const { user } = useAuth();
  const qc = useQueryClient();

  const summaryQuery = useQuery({
    queryKey: isTemplate 
      ? [`reviews-template-summary`, projectId]
      : createQueryKey.reviewsSummary(projectId),
    queryFn: () => isTemplate 
      ? reviewAPI.getTemplateSummary(projectId)
      : reviewAPI.getSummary(projectId),
    select: (res) => res.data.data,
  });

  const [filters, setFilters] = useState({ q: '', sort: 'recent', minRating: null });

  const listQuery = useInfiniteQuery({
    queryKey: isTemplate
      ? [`reviews-template-list`, projectId, filters]
      : createQueryKey.reviewsList(projectId, filters),
    queryFn: ({ pageParam }) => isTemplate
      ? reviewAPI.getTemplateList(projectId, { ...filters, cursor: pageParam })
      : reviewAPI.getList(projectId, { ...filters, cursor: pageParam }),
    getNextPageParam: (lastPage) => lastPage.data.data.nextCursor || undefined,
    select: (data) => ({
      pages: data.pages.map(p => p.data.data.items),
      next: data.pages[data.pages.length - 1].data.data.nextCursor,
    }),
  });

  const items = useMemo(() => (listQuery.data?.pages || []).flat(), [listQuery.data]);

  // We keep endpoint for convenience (e.g., to highlight your latest), but CTA is always "Review project"
  // my review query disabled for now (multiple reviews allowed)

  const handleDelete = async (review) => {
    if (!window.confirm('Delete your review?')) return;
    try {
    await reviewAPI.remove(review.id);
    await Promise.all([
        qc.invalidateQueries(isTemplate ? [`reviews-template-summary`, projectId] : createQueryKey.reviewsSummary(projectId)),
        qc.invalidateQueries(isTemplate ? [`reviews-template-list`, projectId, filters] : createQueryKey.reviewsList(projectId, filters)),
      qc.invalidateQueries(createQueryKey.myReview(projectId)),
    ]);
    } catch (error) {
      console.error('Failed to delete review:', error);
      alert('Failed to delete review. Please try again.');
    }
  };

  // const isLoading = summaryQuery.isLoading || listQuery.isLoading;

  return (
    <section className="mt-4" id="reviews">
      <h3 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white mb-3">Reviews</h3>
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-20">
        {/* Left column: score + histogram + call-to-action (sticky on desktop) */}
        <div className="md:sticky md:top-20 self-start space-y-4">
          <div className="mt-8">
            <div className="flex items-center gap-3">
              <div className="text-3xl lg:text-4xl font-semibold text-gray-900 dark:text-white leading-none">{(summaryQuery.data?.average || 0).toFixed(1)}</div>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-gray-900 dark:text-white"><path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.786 1.401 8.172L12 18.897l-7.335 3.872 1.401-8.172L.132 9.211l8.2-1.193z"/></svg>
              <div className="ml-2">
                <div className="text-sm text-gray-800 dark:text-gray-200">
                  {isTemplate 
                    ? `${summaryQuery.data?.userCount || summaryQuery.data?.count || 0} reviews from ${summaryQuery.data?.userCount || summaryQuery.data?.count || 0} users`
                    : `${summaryQuery.data?.count || 0} reviews`
                  }
                </div>
                {!isTemplate && (
                <div className="text-xs text-gray-500 dark:text-gray-400">{Math.max(summaryQuery.data?.count || 0, 0)} projects</div>
                )}
              </div>
            </div>
            <div className="mt-4">
              <RatingHistogram variant="sidebar" histogram={summaryQuery.data?.histogram || {}} count={summaryQuery.data?.count || 0} />
            </div>
            {!isTemplate && (
              <button onClick={onOpenReview} className="mt-5 w-full px-5 py-3 rounded-full bg-gray-900 dark:bg-white text-white dark:text-black font-medium">Post Review</button>
            )}
            {isTemplate && (
              <div className="mt-5 text-xs text-gray-500 dark:text-gray-400 text-center">
                Start a project to leave a review
              </div>
            )}
          </div>
        </div>

        {/* Right column: search + list */}
        <div>
          <div className="mb-4 md:hidden">
            <ReviewsToolbar value={filters} onChange={setFilters} compact />
          </div>
          <div className="hidden md:block mb-4">
            <ReviewsToolbar value={filters} onChange={setFilters} />
          </div>

          <div className="space-y-3">
            {items.length === 0 && !listQuery.isLoading ? (
              <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center bg-white dark:bg-gray-800">
                <div className="text-3xl lg:text-5xl mb-4">⭐</div>
                <div className="text-base lg:text-lg font-medium text-gray-900 dark:text-white">No reviews yet</div>
                {/* <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">Be the first to share your experience for this project.</div> */}
                {/* <button onClick={() => onOpenReview(null)} className="mt-6 px-5 py-3 rounded-full bg-gray-900 dark:bg-white text-white dark:text-black font-medium">Write a review</button> */}
              </div>
            ) : (
              <>
                {items.map((r) => (
                  <ReviewCard key={r.id} review={r} onEdit={() => onOpenReview(r)} onDelete={r.userId === user?.id ? handleDelete : undefined} />
                ))}
                {listQuery.hasNextPage && (
                  <div className="text-center">
                    <button onClick={() => listQuery.fetchNextPage()} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-700">Load more</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}


